/* eslint-disable sort-imports */
/**
 * Manual control — the stateful half.
 *
 * Owns everything a move needs at runtime: the per-assignment target cache
 * fetched on drag start, the Shift-for-whole-step modifier, the confirm
 * dialog a 409 (or a known-unqualified target) opens, the toast with its
 * Undo, and the gap the last move left behind. Every write goes through
 * `/RosterAssignments/{id}/move` and is followed by a refetch — the board
 * never overlays anything on the server's rows.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import rosterAssignmentsApi, {
	ExtendRequest,
	ManualAssignmentRequest,
	MoveGap,
	MoveRequest,
	MoveTargetPhase,
	MoveTargets,
	RosterAssignment,
	SuggestCandidate,
} from "../../../../../services/roster-assignments-api";
import { apiErrorDetails } from "../../../../../utils/api-error";
import { dayStartMs, hhmm, hoursInto, localDay, weekEndIso } from "../../../utils/time";
import { ConfirmFlag, confirmFlagFor, indexTargets } from "../utils/drop-state";
import { canLend } from "../utils/lend-window";
import { setPendingDraftCount } from "../utils/pending-drafts";
import { composeExtendToast, defaultUntilDate, primaryRole } from "../utils/pin-range";
import { weekBlocksOf } from "../utils/remove-week";
import { MoveTarget, planLendUndo, planUndo, UndoPlan } from "../utils/undo";

export interface MoveConfirm {
	assignmentId: number
	target: MoveTarget
	request: MoveRequest
	/** The flag the confirm sets when accepted. */
	flag: ConfirmFlag
	message: string
}

export interface MoveToast {
	severity: "success" | "error" | "info"
	message: string
	/** Second line — the server's warnings. */
	detail?: string
	undo?: UndoPlan
	/** A removed block the toast's Undo recreates (POST /manual, then unpin when it had not been pinned). */
	restore?: { request: ManualAssignmentRequest; wasPinned: boolean; alsoDelete?: number[] }
	/** Blocks a whole-week removal took off: the toast's Undo puts each back. */
	restoreAll?: { request: ManualAssignmentRequest; wasPinned: boolean }[]
	/** The block a successful move placed: the toast offers to keep it longer. */
	keep?: { assignmentId: number }
	/** A block a drop just created: the toast's Undo deletes it again. */
	remove?: { assignmentId: number; workerName: string }
}

/** The "Remove from step?" confirm, open for one block. */
export interface RemovePrompt {
	assignmentId: number
	workerName: string
	/** "Remove Dana Reyes from 75 CFT 06:00 – 14:00? …" */
	message: string
	/** The block's window (plant-local ISO), for "remove for a while". */
	startTime: string
	endTime: string
	/** The step the block is on. */
	stepTitle: string
	/** The block's line, and how many blocks the person has on it that week (for "whole week"). */
	workstation: string
	weekBlocks: number
}

/** The "Until…" date prompt, open for one block. */
export interface UntilPrompt {
	assignmentId: number
	workerName: string
	workstation: string | null
	/** Earliest date the picker allows — the block's own day. */
	minDate: string
	defaultDate: string
}

export interface PinToLineArgs {
	workerId: string
	workerName: string
	workstation: string
}
/** "Move from a time…": the block being cut and the steps it could go to. */
export interface SplitPrompt {
	assignment: RosterAssignment
	/** Steps the rest of the block could move to, graded for the block's window; null while loading. */
	targets: MoveTargets | null
	/** A refusal the server lets the supervisor override (unqualified / overtime). */
	confirm: { flag: "allowUnqualified" | "allowOvertime"; message: string; at: string; phase: MoveTargetPhase } | null
}
/**
 * A card dropped on another step: move the whole block, or only for a while
 * and bring the person back. `warning` is what the drop already knows (not
 * qualified); `confirm` a refusal of the lend the server lets them override.
 */
export interface HowPrompt {
	assignment: RosterAssignment
	target: Extract<MoveTarget, { kind: "phase" }>
	request: MoveRequest
	/** The target's grade, for its step window and caption. */
	phase: MoveTargetPhase | null
	warning: string | null
	confirm: { flag: ConfirmFlag; message: string; at: string; until: string } | null
}
/** "Swab on Glatt": the target step's product and line, as far as they are known. */
export const destinationOf = (phase: MoveTargetPhase | null, target: Extract<MoveTarget, { kind: "phase" }>): string => {
	const product = phase?.productName;
	const line = phase?.workstation;
	if (product && line) return `${product} on ${line}`;
	return line ?? product ?? target.label ?? "another step";
};

/** A bench chip dropped on a line while the person already works elsewhere that week. */
export interface PinAsk {
	args: PinToLineArgs
	message: string
}
/**
 * A placement drafted on the board and not yet saved: it becomes a standing
 * pin when Allocate runs. `force` = the person was on another line this week
 * and the supervisor chose to move them (their old blocks go when the week is
 * re-allocated).
 */
export interface DraftPin extends PinToLineArgs {
	force: boolean
}

/**
 * A name dropped on a step for one shift: a manual block, saved at once.
 * The window is the open slot when the group has one, else the step's shift.
 */
export interface PlaceRequest {
	workerId: string
	workerName: string
	workOrderId: number
	phaseId: number | null
	jobRoleId: number | null
	jobRoleName: string
	/** Absolute ISO window the block will cover. */
	start: string
	end: string
	workstation: string
	stepTitle: string
}
export interface PlaceOptions {
	/** The person is on another line in that window; remove those blocks and place them here. */
	moveFromElsewhere?: boolean
	allowUnqualified?: boolean
	/** Outside their own shift that day; the block is saved as overtime and they are told. */
	overtime?: boolean
}
/** Something the supervisor must confirm before a dropped name is placed. */
export interface PlaceAsk {
	request: PlaceRequest
	options: PlaceOptions
	kind: "elsewhere" | "unqualified" | "overtime"
	title: string
	message: string
	confirmLabel: string
}

export interface DragInfo {
	assignmentId: number
	label: string
	/** yyyy-MM-dd of the day cell the card was picked up from (week · by line). */
	dayIso?: string
}

export interface UseManualMovesArgs {
	locationId: number
	/** The rows as currently loaded — snapshotted before a refetch for undo. */
	assignments: RosterAssignment[]
	/** Monday of the visible week — "for the week" ranges run to its Sunday. */
	weekStart: Date
	/** The day on screen; the target grades are only good for it. */
	selectedDate?: Date
	/** A card picked up by click (not drag) — its targets are graded the same way. */
	selectedId?: number | null
	/** Refetch the range after any write. */
	onChanged: () => void | Promise<void>
	/** Refetch the week's standing pins after a write that created or cut one. */
	onPinsChanged?: () => void | Promise<void>
	/** "Replace…" on a card: the caller opens the fill picker for the block's step / phase / window. */
	onReplaceRequest?: (assignment: RosterAssignment) => void
	/** The step a block is on, for the remove confirm; falls back to "step #id". */
	stepTitleFor?: (workOrderId: number | undefined) => string
	/** Kept for callers; drafts are now applied by the board's own Allocate button. */
	onReallocateWeek?: () => void | Promise<void>
}

const errorMessage = (err: unknown) => apiErrorDetails(err, "The move could not be saved.");

export const describeTarget = (target: MoveTarget): string =>
	target.kind === "worker" ? target.workerName : target.label || `step #${target.workOrderId}`;

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? "" : "s"}`;

export function useManualMoves({
	locationId,
	assignments,
	weekStart,
	selectedDate,
	selectedId = null,
	onChanged,
	onPinsChanged,
	onReplaceRequest,
	stepTitleFor,
}: UseManualMovesArgs) {
	const [dragging, setDragging] = useState<DragInfo | null>(null);
	const [splitPrompt, setSplitPrompt] = useState<SplitPrompt | null>(null);
	const [howPrompt, setHowPrompt] = useState<HowPrompt | null>(null);
	const [pinAsk, setPinAsk] = useState<PinAsk | null>(null);
	const [draftPins, setDraftPins] = useState<DraftPin[]>([]);
	const [placeAsk, setPlaceAsk] = useState<PlaceAsk | null>(null);
	const [removePrompt, setRemovePrompt] = useState<RemovePrompt | null>(null);
	const [shiftHeld, setShiftHeld] = useState(false);
	const [confirm, setConfirm] = useState<MoveConfirm | null>(null);
	const [toast, setToast] = useState<MoveToast | null>(null);
	const [untilPrompt, setUntilPrompt] = useState<UntilPrompt | null>(null);
	const [gap, setGap] = useState<MoveGap | null>(null);
	const [busy, setBusy] = useState(false);

	// Read by the async callbacks below (undo snapshots, the block behind an
	// extend); written after commit, not during render.
	const assignmentsRef = useRef(assignments);
	useEffect(() => {
		assignmentsRef.current = assignments;
	}, [assignments]);
	const draftPinsRef = useRef(draftPins);
	useEffect(() => {
		draftPinsRef.current = draftPins;
		// Anything that can take the user off the board (refresh, a scheduler
		// tab, the week stepper) asks first while drafts are pending.
		setPendingDraftCount(draftPins.length);
	}, [draftPins]);
	useEffect(() => () => setPendingDraftCount(0), []);
	const shiftRef = useRef(false);

	// The target cache lives in a ref so the drag callbacks keep one identity
	// for the life of the board; `targetsVersion` is bumped when a grade lands
	// so the index below (and the rows reading it) re-render.
	const targetsRef = useRef<Record<number, MoveTargets>>({});
	const [targetsVersion, setTargetsVersion] = useState(0);
	const inFlight = useRef(new Set<number>());
	// Every invalidation moves the token on; a response carrying an older
	// token graded rows that are gone and is dropped.
	const requestToken = useRef(0);

	// The HTML5 backend swallows keyboard events for the length of a drag, but
	// every dragover carries the modifier state — so that is where Shift is read.
	useEffect(() => {
		const onDragOver = (event: DragEvent) => {
			if (event.shiftKey !== shiftRef.current) {
				shiftRef.current = event.shiftKey;
				setShiftHeld(event.shiftKey);
			}
		};
		const reset = () => {
			shiftRef.current = false;
			setShiftHeld(false);
		};
		window.addEventListener("dragover", onDragOver);
		window.addEventListener("dragend", reset);
		window.addEventListener("drop", reset);
		return () => {
			window.removeEventListener("dragover", onDragOver);
			window.removeEventListener("dragend", reset);
			window.removeEventListener("drop", reset);
		};
	}, []);

	// A refetch means the grading may be stale — drop the cache.
	const invalidateTargets = useCallback(() => {
		requestToken.current += 1;
		inFlight.current.clear();
		if (Object.keys(targetsRef.current).length) {
			targetsRef.current = {};
			setTargetsVersion((v) => v + 1);
		}
	}, []);

	// New rows, another day or another week: whatever was graded is for a
	// board that is no longer on screen.
	useEffect(() => {
		invalidateTargets();
	}, [assignments, selectedDate, weekStart, invalidateTargets]);

	const loadTargets = useCallback(
		async (assignmentId: number) => {
			if (targetsRef.current[assignmentId] || inFlight.current.has(assignmentId)) {
				return;
			}
			const token = requestToken.current;
			inFlight.current.add(assignmentId);
			try {
				const result = await rosterAssignmentsApi.getMoveTargets(assignmentId, locationId);
				if (token !== requestToken.current) {
					return;
				}
				targetsRef.current = { ...targetsRef.current, [assignmentId]: result };
				setTargetsVersion((v) => v + 1);
			} catch (err) {
				console.error("Failed to load move targets:", err);
			} finally {
				inFlight.current.delete(assignmentId);
			}
		},
		[locationId]
	);

	const beginDrag = useCallback(
		(info: DragInfo) => {
			setDragging(info);
			void loadTargets(info.assignmentId);
		},
		[loadTargets]
	);
	const endDrag = useCallback(() => setDragging(null), []);

	// A card picked up by click needs its grades just like a dragged one, or the
	// click-then-click path would send moves the server has to refuse.
	useEffect(() => {
		if (selectedId !== null && selectedId !== undefined) {
			void loadTargets(selectedId);
		}
	}, [selectedId, loadTargets]);

	const activeId = dragging ? dragging.assignmentId : selectedId;
	const targetIndex = useMemo(
		() => indexTargets(activeId !== null && activeId !== undefined ? targetsRef.current[activeId] : undefined),
		// targetsVersion is the signal that the ref's contents changed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[activeId, targetsVersion]
	);

	const changed = useCallback(async () => {
		invalidateTargets();
		await onChanged();
	}, [invalidateTargets, onChanged]);

	const performMove = useCallback(
		async (assignmentId: number, target: MoveTarget, request: MoveRequest) => {
			setBusy(true);
			try {
				const before = assignmentsRef.current;
				const result = await rosterAssignmentsApi.move(assignmentId, locationId, request);
				const movedIds = result.movedAssignmentIds?.length ? result.movedAssignmentIds : [assignmentId];
				const moved = before.find((a) => a.id === assignmentId);
				const undo = planUndo(movedIds, before, target, locationId);
				const headline = `${moved?.workerName ?? "Assignment"} → ${describeTarget(target)}${
					request.wholeStep ? " · whole step" : ""
				}`;
				const warnings = (result.warnings ?? []).filter(Boolean);
				setToast({
					severity: warnings.length ? "info" : "success",
					message: warnings.length ? `${headline}. ${warnings.join(" ")}` : `${headline}. Saved and pinned.`,
					undo,
					keep: { assignmentId: movedIds[0] },
				});
				setGap(result.gap ?? null);
				setConfirm(null);
				await changed();
			} catch (err) {
				const { status, message, code } = errorMessage(err);
				const flag = status === 409 ? confirmFlagFor(message, code) : null;
				if (flag) {
					setConfirm({ assignmentId, target, request, flag, message });
				} else {
					setConfirm(null);
					setToast({ severity: "error", message });
				}
			} finally {
				setBusy(false);
			}
		},
		[locationId, changed]
	);

	/**
	 * Entry point for a drop or a click-then-click handoff. A target the
	 * client already knows is unqualified asks before sending; anything else
	 * is sent and the server decides.
	 */
	const requestMove = useCallback(
		(
			assignmentId: number,
			target: MoveTarget,
			options: { wholeStep?: boolean; confirmFirst?: string | null } = {}
		) => {
			const wholeStep = options.wholeStep ?? shiftRef.current;
			const request: MoveRequest =
				target.kind === "worker"
					? { targetWorkerId: target.workerId, targetWorkerName: target.workerName, wholeStep }
					: { targetWorkOrderId: target.workOrderId, targetPhaseId: target.phaseId, wholeStep };
			// A drop on another step asks "whole block or for a while?" when the
			// block is long enough to lend part of it.
			const assignment = assignmentsRef.current.find((a) => a.id === assignmentId);
			if (target.kind === "phase" && !wholeStep && assignment && canLend(assignment.startTime, assignment.endTime)) {
				const phase =
					targetsRef.current[assignmentId]?.phases.find((p) => p.workOrderId === target.workOrderId && p.phaseId === target.phaseId) ?? null;
				setHowPrompt({ assignment, target, request, phase, warning: options.confirmFirst ?? null, confirm: null });
				return;
			}
			if (options.confirmFirst) {
				setConfirm({
					assignmentId,
					target,
					request,
					flag: "allowUnqualified",
					message: options.confirmFirst,
				});
				return;
			}
			void performMove(assignmentId, target, request);
		},
		[performMove]
	);

	const acceptConfirm = useCallback(
		(wholeStep: boolean) => {
			if (!confirm) {
				return;
			}
			void performMove(confirm.assignmentId, confirm.target, {
				...confirm.request,
				wholeStep,
				[confirm.flag]: true,
			});
		},
		[confirm, performMove]
	);
	const cancelConfirm = useCallback(() => setConfirm(null), []);

	/**
	 * Put the last move back. Every restore is attempted even when one fails;
	 * the toast reports "n of m restored" and keeps Undo for whatever is still
	 * out of place. Blocks the loaded week never had are named, not skipped.
	 */
	const undo = useCallback(async () => {
		const remove = toast?.remove;
		if (remove) {
			setBusy(true);
			try {
				await rosterAssignmentsApi.delete(remove.assignmentId);
				setToast({ severity: "success", message: `${remove.workerName}'s block was removed.` });
				await changed();
			} catch (err) {
				setToast({ severity: "error", message: `Could not undo: ${errorMessage(err).message}` });
			} finally {
				setBusy(false);
			}
			return;
		}
		const restoreAll = toast?.restoreAll;
		if (restoreAll?.length) {
			setBusy(true);
			try {
				const results = await Promise.allSettled(
					restoreAll.map(async ({ request, wasPinned }) => {
						const created = await rosterAssignmentsApi.manualAssign(request);
						if (!wasPinned && created?.id) await rosterAssignmentsApi.unpin(created.id);
					})
				);
				const back = results.filter((r) => r.status === "fulfilled").length;
				setToast({
					severity: back === restoreAll.length ? "success" : "error",
					message: back === restoreAll.length ? `${restoreAll[0].request.workerName} is back for the week.` : `${back} of ${restoreAll.length} blocks restored.`,
				});
				await changed();
			} finally {
				setBusy(false);
			}
			return;
		}
		const restore = toast?.restore;
		if (restore) {
			setBusy(true);
			try {
				// A partial removal left pieces either side: they go before the block comes back whole.
				await Promise.allSettled((restore.alsoDelete ?? []).map((id) => rosterAssignmentsApi.remove(id)));
				const created = await rosterAssignmentsApi.manualAssign(restore.request);
				if (!restore.wasPinned && created?.id) {
					await rosterAssignmentsApi.unpin(created.id);
				}
				setToast({ severity: "success", message: `${restore.request.workerName} is back on the step.` });
				await changed();
			} catch (err) {
				setToast({ severity: "error", message: `Could not restore: ${errorMessage(err).message}` });
			} finally {
				setBusy(false);
			}
			return;
		}
		const plan = toast?.undo;
		if (!plan) {
			return;
		}
		setBusy(true);
		try {
			const results = await Promise.allSettled(
				plan.moves.map((move) => rosterAssignmentsApi.move(move.id, plan.locationId, move.request))
			);
			const restored = new Set<number>();
			const remaining: UndoPlan["moves"] = [];
			const errors: string[] = [];
			results.forEach((result, i) => {
				if (result.status === "fulfilled") {
					restored.add(plan.moves[i].id);
				} else {
					remaining.push(plan.moves[i]);
					errors.push(errorMessage(result.reason).message);
				}
			});
			// Only a block that is back where it was should lose its pin.
			const unpinResults = await Promise.allSettled(
				plan.unpinIds.filter((id) => restored.has(id)).map((id) => rosterAssignmentsApi.unpin(id))
			);
			const unpinned = new Set(
				plan.unpinIds.filter((id) => restored.has(id)).filter((_, i) => unpinResults[i].status === "fulfilled")
			);
			const unpinLeft = plan.unpinIds.filter((id) => restored.has(id) && !unpinned.has(id));

			const total = plan.moves.length;
			const outside = plan.missingIds.length
				? ` Could not restore ${plural(plan.missingIds.length, "block")} outside the loaded week.`
				: "";
			if (remaining.length === 0) {
				if (plan.alsoUnpinIds?.length) {
					await Promise.allSettled(plan.alsoUnpinIds.map((id) => rosterAssignmentsApi.unpin(id)));
				}
				setToast({
					severity: outside ? "info" : "success",
					message: `${total === 1 ? "Move undone." : `All ${total} blocks restored.`}${outside}`,
					undo:
						unpinLeft.length
							? { locationId: plan.locationId, moves: [], unpinIds: unpinLeft, missingIds: [] }
							: undefined,
				});
				setGap(null);
			} else {
				setToast({
					severity: "error",
					message: `${restored.size} of ${total} restored${errors[0] ? ` — ${errors[0]}` : ""}.${outside}`,
					undo: {
						locationId: plan.locationId,
						moves: remaining,
						unpinIds: [...unpinLeft, ...plan.unpinIds.filter((id) => !restored.has(id))],
						missingIds: plan.missingIds,
					},
				});
			}
			await changed();
		} catch (err) {
			setToast({ severity: "error", message: `Could not undo: ${errorMessage(err).message}` });
		} finally {
			setBusy(false);
		}
	}, [toast, changed]);

	const unpin = useCallback(
		async (assignmentId: number) => {
			setBusy(true);
			try {
				await rosterAssignmentsApi.unpin(assignmentId);
				setToast({ severity: "success", message: "Unpinned — Allocate may change this assignment again." });
				await changed();
			} catch (err) {
				setToast({ severity: "error", message: errorMessage(err).message });
			} finally {
				setBusy(false);
			}
		},
		[changed]
	);

	const pinsChanged = useCallback(async () => {
		await onPinsChanged?.();
	}, [onPinsChanged]);

	/**
	 * Keep a block longer: pins it and creates a standing pin for the worker
	 * on its line through the Sunday of its week ("week") or a chosen date
	 * ("until"). The server re-pins allocated days in the range and frees the
	 * worker's auto blocks elsewhere; the toast reports what it did.
	 */
	const extend = useCallback(
		async (assignmentId: number, scope: ExtendRequest["scope"], untilDate?: string) => {
			setBusy(true);
			try {
				const block = assignmentsRef.current.find((a) => a.id === assignmentId);
				const result = await rosterAssignmentsApi.extend(assignmentId, locationId, { scope, untilDate });
				const { message, detail } = composeExtendToast(result, block?.workerName ?? "Worker", block?.workstation);
				setToast({ severity: detail ? "info" : "success", message, detail });
				setUntilPrompt(null);
				await changed();
				await pinsChanged();
			} catch (err) {
				setToast({ severity: "error", message: errorMessage(err).message });
			} finally {
				setBusy(false);
			}
		},
		[locationId, changed, pinsChanged]
	);

	/** Open the "Until…" date prompt for a block; defaults to the Sunday that closes the visible week. */
	const openUntil = useCallback(
		(assignmentId: number) => {
			const block = assignmentsRef.current.find((a) => a.id === assignmentId);
			const blockDay = block ? localDay(new Date(block.startTime)) : localDay(weekStart);
			const weekSunday = defaultUntilDate(weekStart);
			setUntilPrompt({
				assignmentId,
				workerName: block?.workerName ?? "Worker",
				workstation: block?.workstation ?? null,
				minDate: blockDay,
				defaultDate: weekSunday >= blockDay ? weekSunday : blockDay,
			});
		},
		[weekStart]
	);
	const cancelUntil = useCallback(() => setUntilPrompt(null), []);
	const confirmUntil = useCallback(
		(untilDate: string) => {
			if (untilPrompt) {
				void extend(untilPrompt.assignmentId, "until", untilDate);
			}
		},
		[untilPrompt, extend]
	);

	/** "Replace…": hand the block's details to the caller, who opens the fill picker in replace mode. */
	const replace = useCallback(
		(assignmentId: number) => {
			const block = assignmentsRef.current.find((a) => a.id === assignmentId);
			if (block) {
				onReplaceRequest?.(block);
			}
		},
		[onReplaceRequest]
	);

	/** "Remove from step": ask first; the slot opens up and Allocate may refill it. */
	const openRemove = useCallback(
		(assignmentId: number) => {
			const block = assignmentsRef.current.find((a) => a.id === assignmentId);
			if (!block) {
				return;
			}
			const dayStart = dayStartMs(new Date(block.startTime));
			const window = `${hhmm(hoursInto(block.startTime, dayStart))} – ${hhmm(hoursInto(block.endTime, dayStart))}`;
			const step = stepTitleFor?.(block.workOrderId) ?? `step #${block.workOrderId}`;
			setRemovePrompt({
				assignmentId,
				workerName: block.workerName,
				message: `Remove ${block.workerName} from ${step} ${window}? The slot opens up and Allocate may refill it.`,
				startTime: block.startTime,
				endTime: block.endTime,
				stepTitle: step,
				workstation: block.workstation ?? "",
				weekBlocks: weekBlocksOf(assignmentsRef.current, block).length,
			});
		},
		[stepTitleFor]
	);
	const cancelRemove = useCallback(() => setRemovePrompt(null), []);
	/**
	 * Remove the block, or only part of it ("for a while"): the block is deleted
	 * and the hours either side are saved back as their own pieces (unpinned
	 * again when the block had not been pinned). Undo puts the block back whole.
	 */
	const confirmRemove = useCallback(
		async (range?: { from: string; until: string } | "week") => {
			if (!removePrompt) {
				return;
			}
			const block = assignmentsRef.current.find((a) => a.id === removePrompt.assignmentId);
			if (range === "week") {
				// The person off this line for the whole week: every block of theirs on it, Monday to Sunday.
				const blocks = block ? weekBlocksOf(assignmentsRef.current, block) : [];
				setBusy(true);
				try {
					const results = await Promise.allSettled(blocks.map((b) => rosterAssignmentsApi.remove(b.id)));
					const removed = blocks.filter((_, i) => results[i].status === "fulfilled");
					setRemovePrompt(null);
					setToast({
						severity: removed.length === blocks.length ? "success" : "error",
						message:
							removed.length === blocks.length
								? `${removePrompt.workerName} is off ${removePrompt.workstation} for the week (${plural(removed.length, "block")}) — those slots are open; Fill may place someone there.`
								: `${removed.length} of ${blocks.length} blocks removed for ${removePrompt.workerName}.`,
						restoreAll: removed.map((b) => ({
							wasPinned: Boolean(b.isManuallyAssigned),
							request: {
								workOrderId: b.workOrderId,
								phaseId: b.laborStandardPhaseId ?? undefined,
								workerId: String(b.workerId),
								workerName: b.workerName,
								jobRoleId: b.jobRoleId ?? undefined,
								jobRoleName: b.jobRoleName,
								shiftId: b.shiftId ?? undefined,
								startTime: b.startTime,
								endTime: b.endTime,
							},
						})),
					});
					await changed();
				} catch (err) {
					setToast({ severity: "error", message: errorMessage(err).message });
				} finally {
					setBusy(false);
				}
				return;
			}
			const partial = Boolean(block && range && (range.from > block.startTime || range.until < block.endTime));
			setBusy(true);
			try {
				await rosterAssignmentsApi.remove(removePrompt.assignmentId);
				const base = block
					? {
							workOrderId: block.workOrderId,
							phaseId: block.laborStandardPhaseId ?? undefined,
							workerId: String(block.workerId),
							workerName: block.workerName,
							jobRoleId: block.jobRoleId ?? undefined,
							jobRoleName: block.jobRoleName,
							shiftId: block.shiftId ?? undefined,
						}
					: null;
				const kept: number[] = [];
				if (partial && block && base && range) {
					const pieces = [
						[block.startTime, range.from],
						[range.until, block.endTime],
					].filter(([from, until]) => until > from);
					for (const [from, until] of pieces) {
						const created = await rosterAssignmentsApi.manualAssign({ ...base, startTime: from, endTime: until, notes: `Off ${removePrompt.stepTitle} ${range.from.slice(11, 16)}–${range.until.slice(11, 16)}` });
						if (created?.id) {
							kept.push(created.id);
							if (!block.isManuallyAssigned) await rosterAssignmentsApi.unpin(created.id);
						}
					}
				}
				setRemovePrompt(null);
				setToast({
					severity: "success",
					message:
						partial && range
							? `${removePrompt.workerName} is off ${removePrompt.stepTitle} ${range.from.slice(11, 16)}–${range.until.slice(11, 16)} — that slot is open; the rest of the block stays.`
							: `${removePrompt.workerName} removed — the slot is open; Allocate may refill it.`,
					restore:
						block && base
							? {
									wasPinned: Boolean(block.isManuallyAssigned),
									request: { ...base, startTime: block.startTime, endTime: block.endTime },
									alsoDelete: kept,
								}
							: undefined,
				});
				await changed();
			} catch (err) {
				setToast({ severity: "error", message: errorMessage(err).message });
			} finally {
				setBusy(false);
			}
		},
		[removePrompt, changed]
	);

	/**
	 * A name dropped on a line (or a work-center chip on a worker row): a
	 * drafted placement for the visible week. Nothing is saved yet — the draft
	 * sits on the board until Allocate, when it becomes a standing pin and the
	 * week is allocated around it. One draft per person: a second drop moves
	 * the draft to the new line.
	 */
	const pinToLine = useCallback(
		({ workerId, workerName, workstation }: PinToLineArgs, options: { force?: boolean } = {}) => {
			// Already on another line this week? Say so and offer to move them —
			// their old blocks are handed back when the week is re-allocated.
			if (!options.force) {
				const elsewhere = assignmentsRef.current.filter(
					(a) => String(a.workerId) === String(workerId) && a.workstation && a.workstation !== workstation
				);
				if (elsewhere.length) {
					const lines = Array.from(new Set(elsewhere.map((a) => a.workstation as string)));
					const days = new Set(elsewhere.map((a) => localDay(new Date(a.startTime)))).size;
					setPinAsk({
						args: { workerId, workerName, workstation },
						message: `${workerName} is already on ${lines.join(" and ")} this week (${plural(days, "day")}). Draft a move to ${workstation}? Nothing changes until you press Allocate; then their ${lines.join(" / ")} blocks are handed back and the week is re-allocated.`,
					});
					return;
				}
			}
			setDraftPins((prev) => [
				...prev.filter((d) => String(d.workerId) !== String(workerId)),
				{ workerId, workerName, workstation, force: Boolean(options.force) },
			]);
			setToast({
				severity: "info",
				message: `${workerName} drafted on ${workstation} for the week. Press Allocate to apply the drafts.`,
			});
		},
		[]
	);
	const cancelPinAsk = useCallback(() => setPinAsk(null), []);
	const confirmPinAsk = useCallback(() => {
		if (!pinAsk) {
			return;
		}
		const { args } = pinAsk;
		setPinAsk(null);
		pinToLine(args, { force: true });
	}, [pinAsk, pinToLine]);
	const removeDraftPin = useCallback((workerId: string) => {
		setDraftPins((prev) => prev.filter((d) => String(d.workerId) !== String(workerId)));
	}, []);
	const clearDraftPins = useCallback(() => setDraftPins([]), []);

	/**
	 * Save the drafted placements as standing pins for the week. Runs just
	 * before Allocate; a draft that fails to save stays on the board and the
	 * allocation is held back so the plan never runs on half the drafts.
	 * Resolves true when every draft went in (or there were none).
	 */
	const commitDraftPins = useCallback(async (): Promise<boolean> => {
		const drafts = draftPinsRef.current;
		if (drafts.length === 0) {
			return true;
		}
		setBusy(true);
		const saved: string[] = [];
		try {
			const startDate = localDay(weekStart);
			const endDate = weekEndIso(weekStart);
			for (const draft of drafts) {
				const role = primaryRole(assignmentsRef.current, draft.workerId);
				await rosterAssignmentsApi.createPin(
					{
						workerId: draft.workerId,
						workerName: draft.workerName,
						workstation: draft.workstation,
						jobRoleId: role.jobRoleId,
						jobRoleName: role.jobRoleName,
						startDate,
						endDate,
						notes: null,
					},
					locationId
				);
				saved.push(String(draft.workerId));
			}
			setDraftPins([]);
			setToast({
				severity: "success",
				message: `${plural(drafts.length, "placement")} saved for the week; allocating…`,
			});
			await pinsChanged();
			return true;
		} catch (err) {
			setDraftPins((prev) => prev.filter((d) => !saved.includes(String(d.workerId))));
			const failed = drafts.length - saved.length;
			setToast({
				severity: "error",
				message: `${errorMessage(err).message} ${plural(failed, "draft")} kept on the board; allocation not run.`,
			});
			if (saved.length) {
				await pinsChanged();
			}
			return false;
		} finally {
			setBusy(false);
		}
	}, [locationId, weekStart, pinsChanged]);

	// ---- place a dropped name on a step for one shift -------------------------

	/**
	 * Save one manual block for a name dropped on a step. Three things can
	 * stop it, each asked once and in this order: they are already on another
	 * line in that window (move them, their other block goes), the server
	 * grades them unqualified for the role, or the window is outside their
	 * own shift (overtime). Already on this line in that window: nothing to
	 * do, the toast says so.
	 */
	const placeWorker = useCallback(
		async (request: PlaceRequest, options: PlaceOptions = {}) => {
			const startMs = new Date(request.start).getTime();
			const endMs = new Date(request.end).getTime();
			const window = `${hhmm(hoursInto(request.start, dayStartMs(new Date(request.start))))}–${hhmm(hoursInto(request.end, dayStartMs(new Date(request.start))))}`;
			const dayLabel = new Date(request.start).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
			const overlapping = assignmentsRef.current.filter(
				(a) =>
					String(a.workerId) === String(request.workerId) &&
					new Date(a.startTime).getTime() < endMs &&
					new Date(a.endTime).getTime() > startMs
			);
			const here = overlapping.filter((a) => a.workstation === request.workstation);
			if (here.length) {
				setToast({
					severity: "info",
					message: `${request.workerName} is already on ${request.workstation} on ${dayLabel}, ${window}.`,
				});
				return;
			}
			const elsewhere = overlapping.filter((a) => a.workstation && a.workstation !== request.workstation);
			if (elsewhere.length && !options.moveFromElsewhere) {
				const lines = Array.from(new Set(elsewhere.map((a) => a.workstation as string)));
				setPlaceAsk({
					request,
					options: { ...options, moveFromElsewhere: true },
					kind: "elsewhere",
					title: "Already working then",
					message: `${request.workerName} is on ${lines.join(" and ")} on ${dayLabel}, ${window}. Move them to ${request.workstation} instead? Their ${lines.join(" / ")} block for that time is removed.`,
					confirmLabel: `Move to ${request.workstation}`,
				});
				return;
			}

			// The server's grade for this person in this window: role fit and
			// whether it is their own shift. Best effort — no grade, no ask.
			let candidate: SuggestCandidate | null = null;
			let graded = false;
			try {
				const result = await rosterAssignmentsApi.suggest({
					workOrderId: request.workOrderId,
					phaseId: request.phaseId ?? undefined,
					start: request.start,
					end: request.end,
					locationId,
					includeOffShift: true,
				});
				graded = true;
				candidate = result.candidates.find((c) => String(c.workerId) === String(request.workerId)) ?? null;
			} catch {
				graded = false;
			}
			const state = candidate?.state?.toLowerCase() ?? (graded ? "unqualified" : "unknown");
			if (state === "unqualified" && !options.allowUnqualified) {
				setPlaceAsk({
					request,
					options: { ...options, allowUnqualified: true },
					kind: "unqualified",
					title: "Not qualified",
					message: `${request.workerName} is not set up as ${request.jobRoleName || "this role"}${candidate?.reason ? ` (${candidate.reason})` : ""}. Place them on ${request.stepTitle} anyway?`,
					confirmLabel: "Place anyway",
				});
				return;
			}
			if (state === "overtime" && !options.overtime) {
				setPlaceAsk({
					request,
					options: { ...options, overtime: true },
					kind: "overtime",
					title: "Outside their shift",
					message: `${dayLabel}, ${window} is outside ${request.workerName}'s own shift${candidate?.hoursThatDay ? ` (${candidate.hoursThatDay} h already that day)` : ""}. Place it as overtime? They are told on their phone.`,
					confirmLabel: "Place as overtime",
				});
				return;
			}

			setBusy(true);
			try {
				for (const block of elsewhere) {
					await rosterAssignmentsApi.delete(block.id);
				}
				const created = await rosterAssignmentsApi.manualAssign({
					workOrderId: request.workOrderId,
					phaseId: request.phaseId ?? undefined,
					workerId: request.workerId,
					workerName: request.workerName,
					jobRoleId: request.jobRoleId ?? undefined,
					jobRoleName: request.jobRoleName,
					startTime: request.start,
					endTime: request.end,
					shiftId: candidate?.shiftId ?? undefined,
					...(options.overtime ? { isOvertime: true, notes: "Overtime: outside own shift" } : {}),
				});
				const moved = elsewhere.length ? ` (moved off ${Array.from(new Set(elsewhere.map((a) => a.workstation))).join(" / ")})` : "";
				setToast({
					severity: options.overtime || elsewhere.length ? "info" : "success",
					message: `${request.workerName} placed on ${request.stepTitle} · ${request.workstation}, ${window}${moved}.`,
					detail: options.overtime ? "Saved as overtime." : undefined,
					remove: { assignmentId: created.id, workerName: request.workerName },
				});
				await changed();
			} catch (err) {
				setToast({ severity: "error", message: errorMessage(err).message });
			} finally {
				setBusy(false);
			}
		},
		[locationId, changed]
	);
	const cancelPlaceAsk = useCallback(() => setPlaceAsk(null), []);
	const confirmPlaceAsk = useCallback(async () => {
		if (!placeAsk) {
			return;
		}
		const { request, options } = placeAsk;
		setPlaceAsk(null);
		await placeWorker(request, options);
	}, [placeAsk, placeWorker]);

	// ---- whole block or for a while -----------------------------------------

	const cancelHow = useCallback(() => setHowPrompt(null), []);
	/** "Whole block": the move the drop would have made, the warning already seen. */
	const chooseWhole = useCallback(() => {
		if (!howPrompt) {
			return;
		}
		const { assignment, target, request, warning } = howPrompt;
		setHowPrompt(null);
		void performMove(assignment.id, target, warning ? { ...request, allowUnqualified: true } : request);
	}, [howPrompt, performMove]);
	/**
	 * "For a while": lend [at, until) to the step and bring the person back.
	 * A refusal the server marks confirmable stays in the prompt with a
	 * "Place anyway"; the next call carries the override.
	 */
	const chooseLend = useCallback(
		async (at: string, until: string, flags: { allowUnqualified?: boolean; allowOvertime?: boolean } = {}) => {
			if (!howPrompt) {
				return;
			}
			const { assignment, target, phase, warning } = howPrompt;
			setBusy(true);
			try {
				const result = await rosterAssignmentsApi.splitMove(assignment.id, locationId, {
					at,
					until,
					targetWorkOrderId: target.workOrderId,
					targetPhaseId: target.phaseId,
					...(warning ? { allowUnqualified: true } : {}),
					...flags,
				});
				const warnings = (result.moved?.warnings ?? []).filter((w) => w && !w.includes(" goes back to "));
				const movedIds = result.moved?.movedAssignmentIds ?? [];
				const stayedIds = [result.stayedAssignmentId, result.returnAssignmentId ?? 0, ...(result.moved?.stayedAssignmentIds ?? [])];
				setToast({
					severity: warnings.length ? "info" : "success",
					message: `${assignment.workerName} → ${destinationOf(phase, target)} ${at.slice(11, 16)}–${until.slice(11, 16)}, then back to ${assignment.workstation ?? "their step"}. ${warnings.join(" ")}`.trim(),
					undo: movedIds.length ? planLendUndo(assignment, movedIds, stayedIds, locationId) : undefined,
				});
				setGap(result.moved?.gap ?? null);
				setHowPrompt(null);
				await changed();
			} catch (err) {
				const { status, message, code } = errorMessage(err);
				const flag = status === 409 ? confirmFlagFor(message, code) : null;
				if (flag) {
					setHowPrompt((current) => (current ? { ...current, confirm: { flag, message, at, until } } : current));
				} else {
					setToast({ severity: "error", message });
				}
			} finally {
				setBusy(false);
			}
		},
		[howPrompt, locationId, changed]
	);

	// ---- mid-shift moves ----------------------------------------------------

	/** "Move from a time…" on a card: load where the rest of the block could go. */
	const openSplit = useCallback(
		async (assignmentId: number) => {
			const assignment = assignmentsRef.current.find((a) => a.id === assignmentId);
			if (!assignment) {
				return;
			}
			setSplitPrompt({ assignment, targets: null, confirm: null });
			try {
				const targets = await rosterAssignmentsApi.getMoveTargets(assignmentId, locationId);
				setSplitPrompt((current) => (current && current.assignment.id === assignmentId ? { ...current, targets } : current));
			} catch (err) {
				setSplitPrompt(null);
				setToast({ severity: "error", message: errorMessage(err).message });
			}
		},
		[locationId]
	);
	const cancelSplit = useCallback(() => setSplitPrompt(null), []);
	/**
	 * Cut the block at `at` (plant-local ISO) and send the rest to the phase.
	 * A refusal the server marks confirmable is shown in the dialog; the next
	 * call carries the override flag.
	 */
	const confirmSplit = useCallback(
		async (at: string, phase: MoveTargetPhase, flags: { allowUnqualified?: boolean; allowOvertime?: boolean } = {}) => {
			if (!splitPrompt) {
				return;
			}
			const { assignment } = splitPrompt;
			setBusy(true);
			try {
				const result = await rosterAssignmentsApi.splitMove(assignment.id, locationId, {
					at,
					targetWorkOrderId: phase.workOrderId,
					targetPhaseId: phase.phaseId,
					...flags,
				});
				const time = at.slice(11, 16);
				const warnings = (result.moved?.warnings ?? []).filter(Boolean);
				setToast({
					severity: warnings.length ? "info" : "success",
					message: `${assignment.workerName} stays on ${assignment.workstation ?? "their step"} until ${time}, then ${phase.workstation ?? "another step"}${phase.jobRole ? ` as ${phase.jobRole}` : ""}. ${warnings.join(" ")}`.trim(),
				});
				setGap(result.moved?.gap ?? null);
				setSplitPrompt(null);
				await changed();
			} catch (err) {
				const { status, message, code } = errorMessage(err);
				const flag = status === 409 ? confirmFlagFor(message, code) : null;
				if (flag) {
					setSplitPrompt((current) => (current ? { ...current, confirm: { flag, message, at, phase } } : current));
				} else {
					setToast({ severity: "error", message });
				}
			} finally {
				setBusy(false);
			}
		},
		[splitPrompt, locationId, changed]
	);

	const dismissToast = useCallback(() => setToast(null), []);
	const dismissGap = useCallback(() => setGap(null), []);

	// One object per change of state, not per render: rows take `manual` as a
	// prop and memoised drop hooks read callbacks off it.
	return useMemo(
		() => ({
			dragging,
			shiftHeld,
			beginDrag,
			endDrag,
			targetIndex,
			requestMove,
			confirm,
			acceptConfirm,
			cancelConfirm,
			toast,
			dismissToast,
			undo,
			unpin,
			extend,
			untilPrompt,
			openUntil,
			cancelUntil,
			confirmUntil,
			pinToLine,
			pinAsk,
			cancelPinAsk,
			confirmPinAsk,
			placeWorker,
			placeAsk,
			cancelPlaceAsk,
			confirmPlaceAsk,
			draftPins,
			removeDraftPin,
			clearDraftPins,
			commitDraftPins,
			splitPrompt,
			openSplit,
			cancelSplit,
			confirmSplit,
			howPrompt,
			cancelHow,
			chooseWhole,
			chooseLend,
			replace,
			removePrompt,
			openRemove,
			cancelRemove,
			confirmRemove,
			gap,
			dismissGap,
			busy,
			changed,
			invalidateTargets,
		}),
		[
			dragging,
			shiftHeld,
			beginDrag,
			endDrag,
			targetIndex,
			requestMove,
			confirm,
			acceptConfirm,
			cancelConfirm,
			toast,
			dismissToast,
			undo,
			unpin,
			extend,
			untilPrompt,
			openUntil,
			cancelUntil,
			confirmUntil,
			pinToLine,
			pinAsk,
			cancelPinAsk,
			confirmPinAsk,
			placeWorker,
			placeAsk,
			cancelPlaceAsk,
			confirmPlaceAsk,
			draftPins,
			removeDraftPin,
			clearDraftPins,
			commitDraftPins,
			splitPrompt,
			openSplit,
			cancelSplit,
			confirmSplit,
			howPrompt,
			cancelHow,
			chooseWhole,
			chooseLend,
			replace,
			removePrompt,
			openRemove,
			cancelRemove,
			confirmRemove,
			gap,
			dismissGap,
			busy,
			changed,
			invalidateTargets,
		]
	);
}

export type ManualMoves = ReturnType<typeof useManualMoves>
